import { useEffect, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../../../../components/recharts/functions";
import { ReMixChart } from "../../../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../../../models/chart_model";
import { renderHourTick } from "../../../../../../../components/recharts/components";
import { R_CHART_TYPES } from "../../../../../../DemoState/const";
import loader from "../../../../../../../components/recharts/loader.gif";
import { getMonthFromDate } from "../../../../../../../extensions/date";
import { normalizeDisplayLabelWithoutUndefined } from "../../../../../../../utility/displayText";

interface SummaryData {
  date: string;
  month: string;
  oar: number;
  gna_cost: number;
  gna_quantum: number;
  tgna_cost: number;
  tgna_quantum: number;
  optimal_gna: number;
  total_cost: number;
  slot: number;
}

interface ScenarioCapacityData {
  scenario_id: number;
  scenario_number: number;
  is_active: boolean;
  is_finalized: boolean;
  dates: Array<{
    year: number;
    date: string;
    sources: Array<{
      source: string;
      total_capacity: string;
    }>;
  }>;
}

export default function MonthVolumeAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  loading,
  data,
}: {
  selectedBlock?: any[];
  refreshKey?: number;
  setError?: any;
  loading: boolean;
  data: any[];
}) {
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);

  // --- OLD CHART CONFIG ---
  const chartSuffixes = [
    {
      suffix: "tgna_quantum",
      displayName: "Optimal TGNA Utilisation",
      stackSuffix: "tgna_quantum",
      stroke: "rgba(255, 194, 158, 1)",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "gna_quantum",
      displayName: "Optimal GNA Utilisation",
      stackSuffix: "gna_quantum",
      stroke: "rgba(241, 147, 92, 1)",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "optimal_gna",
      displayName: "Optimal GNA",
      stackSuffix: "optimal_gna",
      stroke: "rgba(52, 101, 109, 1)",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
      isDash: true
    },
    {
      suffix: "oar",
      displayName: "Total Open Access Requirement",
      stackSuffix: "oar",
      stroke: "rgba(17, 141, 255, 1)",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
    },
  ];

  const [selectedMetrics] = useState<any[]>([
    chartSuffixes[0],
    chartSuffixes[1],
    chartSuffixes[2],
    chartSuffixes[3],
  ]);
  const [selectedChartType] = useState(R_CHART_TYPES[1]);

  // --- COST COLORS (Distinct from DailyVolume) ---
  const costColors: any = {
    base_cost: '#2ecc71', // Green
    added_scenario_cost: '#e74c3c', // Red

    // BESS Cost Colors
    bess_operation_cost: '#3498db', // Blue
    bess_cost_consumed: '#e67e22', // Orange
    bess_cost_saved: '#9b59b6'   // Purple
  };

  useEffect(() => {
    // Debugging Logs


    if (!data || !Array.isArray(data) || data.length === 0) {
      setChartData([]);
      setChartKeys([]);
      return;
    }

    // Check for scenario-based data (years or dates)
    const firstItem = data[0];
    const hasDataYearwise = firstItem && 'data_yearwise' in firstItem && Array.isArray(firstItem.data_yearwise);
    const hasYears = firstItem && 'years' in firstItem && Array.isArray(firstItem.years);
    const hasDates = firstItem && 'dates' in firstItem && Array.isArray(firstItem.dates);

    if (hasDataYearwise || hasYears || hasDates) {

      const transformedData: any[] = [];
      const allYears = new Set<string>();

      data.forEach((scenario: any) => {
        const timeArray = scenario.data_yearwise || scenario.years || scenario.dates || [];
        timeArray.forEach((item: any) => {
          if (item.year) allYears.add(item.year.toString());
          else if (item.date) allYears.add(item.date.split('-')[0]);
        });
      });

      Array.from(allYears).sort().forEach((year) => {
        const row: any = { year: year.toString() };

        data.forEach((scenario: any, index: number) => {

          const sNum = scenario.scenario_number !== undefined ? scenario.scenario_number : (index + 1);
          const scenarioLabel = `Scenario_${sNum}`;
          const timeArray = scenario.data_yearwise || scenario.years || scenario.dates || [];

          const yearData = timeArray.find((y: any) => {
            if (y.year) return y.year.toString() === year;
            if (y.date) return y.date.startsWith(year);
            return false;
          });

          if (yearData) {
            const { bess_operation_cost, bess_cost_consumed, bess_cost_saved, base_cost, added_scenario_cost } = yearData;

            // Map BESS Cost Metrics if present
            if (bess_operation_cost !== undefined || bess_cost_consumed !== undefined || bess_cost_saved !== undefined) {
              row[`${scenarioLabel}_bess_operation_cost`] = Number(bess_operation_cost || 0);
              row[`${scenarioLabel}_bess_cost_consumed`] = Number(bess_cost_consumed || 0);
              row[`${scenarioLabel}_bess_cost_saved`] = Number(bess_cost_saved || 0);
            }

            // Map Base/Added Cost Metrics if present (Concurrent support)
            if (base_cost !== undefined) {
              row[`${scenarioLabel}_base_cost`] = Number(base_cost || 0);
            }
            if (added_scenario_cost !== undefined) {
              row[`${scenarioLabel}_added_scenario_cost`] = Number(added_scenario_cost || 0);
            }
          }
        });
        transformedData.push(row);
      });

      const allKeys = new Set<string>();
      transformedData.forEach(row => {
        Object.keys(row).forEach(key => { if (key !== 'year') allKeys.add(key); });
      });
      const keys = Array.from(allKeys);

      setChartData(transformedData);
      setChartKeys(keys);


    } else if (data[0] && ('base_cost' in data[0] || 'added_scenario_cost' in data[0])) {
      // FLAT STRUCTURE HANDLING

      const transformedData: any[] = [];
      const allYears = new Set<string>();

      data.forEach((item: any) => {
        if (item.year) allYears.add(item.year.toString());
        else if (item.date) allYears.add(item.date.split('-')[0]);
      });

      Array.from(allYears).sort().forEach((year) => {
        const row: any = { year: year.toString() };
        const yearItems = data.filter((d: any) => (d.year && d.year.toString() === year) || (d.date && d.date.startsWith(year)));

        yearItems.forEach((item: any) => {
          const sNum = item.scenario_number !== undefined ? item.scenario_number : item.scenario_id;
          if (sNum !== undefined) {
            const scenarioLabel = `Scenario_${sNum}`;
            const baseKey = `${scenarioLabel}_base_cost`;
            const addedKey = `${scenarioLabel}_added_scenario_cost`;

            row[baseKey] = Number(item.base_cost || 0);
            row[addedKey] = Number(item.added_scenario_cost || 0);
          }
        });
        transformedData.push(row);
      });

      const allKeys = new Set<string>();
      transformedData.forEach(row => {
        Object.keys(row).forEach(key => { if (key !== 'year') allKeys.add(key); });
      });
      const keys = Array.from(allKeys);

      setChartData(transformedData);
      setChartKeys(keys);

    } else {
      // --- OLD LOGIC ---
      let filteredData = data;
      // if (selectedBlock && selectedBlock.length > 0) {
      //   const blocks = selectedBlock.map((b: any) => Number(b.value ?? b));
      //   filteredData = filteredData.filter((row: any) =>
      //     blocks.includes(Number(row.time_slot))
      //   );
      // }

      const par: ChartDataParams = {
        keys: ["date", "slot", "month"],
        tableKeys: ["date", "slot", "month"],
        groupBy: [""],
        volumeKey: ``,
        aggregationKeys: ["gna_quantum", "tgna_quantum", "optimal_gna", "oar"],
        averageKeys: [],
        averageDuplicationKeys: ["date", "slot", "month"],
        priceKey: "",
        data: filteredData,
      };

      const cData = FormatChartData(par);
      setChartData(cData.data);
      setChartKeys(cData.keys);
    }
  }, [data, selectedBlock]);

  const createChartObject = (
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    fill: string,
    yAxisId: string,
    isDash?: boolean
  ) => {
    return {
      dataKey: suffix,
      name: displayName,
      legendColor: fill === "none" ? stroke : fill,
      type: chartType,
      unit: unit,
      stroke: stroke,
      fill: fill,
      yAxisId: yAxisId,
      stackId: stackSuffix || undefined,
      isDash: isDash
    };
  };

  const createOldChartObject = (
    key: string,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    legendColor: string,
    yAxisId: string,
    isDash: boolean
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
      legendColor: legendColor,
      type: ["optimal_gna", "oar"].includes(suffix)
        ? ChartType.Line
        : selectedChartType.value,
      unit: unit,
      stroke: stroke,
      yAxisId: yAxisId,
      stackId: undefined,
      isDash: isDash
    };
  };

  function getLegends() {
    if (chartData.length === 0) return [];

    if (chartKeys.length > 0 && chartKeys[0].includes('Scenario')) {
      return chartKeys.map((key: string) => {
        const parts = key.split('_');
        const scenarioNum = parts[1];
        const metric = parts.slice(2).join('_'); // base_cost or added_scenario_cost

        const stroke = costColors[metric] || '#000000';

        let label = "";
        if (metric === 'base_cost') label = "Base Cost";
        else if (metric === 'added_scenario_cost') label = "Added Scenario Cost";
        else label = metric;

        const displayName = `Scenario ${scenarioNum} ${label}`;
        const stackSuffix = `${parts[0]}_${scenarioNum}`;

        return createChartObject(
          key,
          displayName,
          stackSuffix,
          ChartType.Bar,
          "INR",
          stroke,
          stroke,
          "left"
        );
      });
    }

    // OLD LEGENDS
    return selectedMetrics.flatMap(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        yAxisId,
        stroke,
        legendColor,
        isDash
      }) =>
        chartKeys.map(
          (key, index) =>
            createOldChartObject(
              key,
              suffix,
              displayName,
              stackSuffix,
              chartType,
              unit,
              stroke,
              legendColor,
              yAxisId,
              isDash
            ) as any
        )
    );
  }

  const isScenario = chartKeys.length > 0 && chartKeys[0].includes('Scenario');

  return (
    <>
      {loading ? (
        <div className="w-full bg-white flex items-center justify-center">
          <img
            src={loader}
            width={200}
            alt="loader"
            className="block mt-[5rem]"
          />
        </div>
      ) : chartData.length === 0 ? (
        <div className="w-full bg-white flex items-center justify-center h-[400px]">
          <p className="text-gray-500 !text-sm">Please Select Scenarios to View Data</p>
        </div>
      ) : (
        <ReMixChart
          chartHeight="calc(100% - 50px)"
          data={chartData}
          showBrush={!isScenario && chartData.length > 96 * 2 ? true : undefined}
          brushStart={BrushStart.Start}
          isTimeSlot={false}
          onlyTitle={true}
          legends={getLegends()}
          yAxisLabel="Cost (INR)"
          nameGap={40}
          xDataKey={isScenario ? "year" : "month"}
          top={10}
          bottom={30}
          xTick={!isScenario ? (value: any, index: any) => {
            return renderHourTick(value, 10);
          } : undefined}
          showToolbox={false}
        />
      )}
    </>
  );
}
